import { MailQuestion, RefreshCw, Undo2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ListPagination } from '@/components/ListPagination';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { ApiError } from '@/core/api';
import { toastApiError } from '@/core/api';
import { formatDate } from '@/lib/format';
import { useInvitations, useResendInvitation, useRevokeInvitation } from '../queries';
import type { InvitationDto, InvitationStatus } from '../types';

type StatusFilter = InvitationStatus | 'all';

const STATUS_VARIANT: Record<InvitationStatus, 'default' | 'secondary' | 'destructive'> = {
  pending: 'default',
  accepted: 'secondary',
  revoked: 'destructive',
  expired: 'secondary',
};

/** Pending/all invitation list with resend + revoke — the Invitations tab of
 *  the Members page (admin sees all, a teacher only their own — server-side). */
export function InvitationsSection() {
  const { t, i18n } = useTranslation(['invitations', 'common']);
  const [status, setStatus] = useState<StatusFilter>('pending');
  const [page, setPage] = useState(1);
  const [pendingRevoke, setPendingRevoke] = useState<InvitationDto | null>(null);

  const invitations = useInvitations({
    ...(status !== 'all' ? { status } : {}),
    page,
  });
  const resend = useResendInvitation();
  const revoke = useRevokeInvitation();

  const rows = invitations.data?.results ?? [];

  async function onResend(invitation: InvitationDto) {
    try {
      await resend.mutateAsync(invitation.public_id);
      toast.success(t('invitations:list.resent'));
    } catch (e) {
      toastApiError(e);
    }
  }

  async function onRevoke(invitation: InvitationDto) {
    try {
      await revoke.mutateAsync(invitation.public_id);
      toast.success(t('invitations:list.revoked'));
    } catch (e) {
      toastApiError(e);
      throw e;
    }
  }

  const targetOf = (inv: InvitationDto): string => {
    if (inv.school_class) return inv.school_class.name;
    if (inv.pupil) return `${inv.pupil.first_name} ${inv.pupil.last_name}`;
    return '—';
  };

  return (
    <div className="space-y-4">
      <Select
        value={status}
        onValueChange={v => {
          setStatus(v as StatusFilter);
          setPage(1);
        }}
      >
        <SelectTrigger aria-label={t('invitations:list.filterLabel')} className="w-40">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="pending">{t('invitations:status.pending')}</SelectItem>
          <SelectItem value="all">{t('invitations:list.all')}</SelectItem>
          <SelectItem value="accepted">{t('invitations:status.accepted')}</SelectItem>
          <SelectItem value="revoked">{t('invitations:status.revoked')}</SelectItem>
          <SelectItem value="expired">{t('invitations:status.expired')}</SelectItem>
        </SelectContent>
      </Select>

      {invitations.isLoading ? (
        <div className="space-y-2" aria-hidden>
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : invitations.isError ? (
        <Card>
          <CardContent className="text-destructive py-10 text-center text-sm">
            {(invitations.error as unknown as ApiError)?.message}
          </CardContent>
        </Card>
      ) : rows.length === 0 ? (
        <Card>
          <CardContent className="text-muted-foreground flex flex-col items-center gap-3 py-14 text-center">
            <MailQuestion className="size-8" />
            <p className="max-w-sm text-sm">
              {status === 'all' ? t('invitations:list.empty') : t('invitations:list.emptyFiltered')}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card className="py-0">
          <CardContent className="px-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="pl-4">{t('invitations:list.table.email')}</TableHead>
                  <TableHead>{t('invitations:list.table.role')}</TableHead>
                  <TableHead>{t('invitations:list.table.target')}</TableHead>
                  <TableHead>{t('invitations:list.table.invitedBy')}</TableHead>
                  <TableHead>{t('invitations:list.table.status')}</TableHead>
                  <TableHead>{t('invitations:list.table.expires')}</TableHead>
                  <TableHead className="pr-4 text-right">{t('common:actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(inv => {
                  const actionable = inv.status === 'pending' || inv.status === 'expired';
                  return (
                    <TableRow key={inv.public_id}>
                      <TableCell className="pl-4 font-medium">{inv.email}</TableCell>
                      <TableCell>{t(`common:roles.${inv.role}`)}</TableCell>
                      <TableCell className="text-muted-foreground">{targetOf(inv)}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {inv.invited_by
                          ? `${inv.invited_by.first_name} ${inv.invited_by.last_name}`
                          : '—'}
                      </TableCell>
                      <TableCell>
                        <Badge variant={STATUS_VARIANT[inv.status] ?? 'secondary'}>
                          {t(`invitations:status.${inv.status}`)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground tabular-nums">
                        {formatDate(inv.expires_at, i18n.language)}
                      </TableCell>
                      <TableCell className="pr-4 text-right">
                        {actionable ? (
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={resend.isPending}
                              onClick={() => void onResend(inv)}
                            >
                              <RefreshCw />
                              {t('invitations:list.resend')}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={revoke.isPending}
                              onClick={() => setPendingRevoke(inv)}
                            >
                              <Undo2 />
                              {t('invitations:list.revoke')}
                            </Button>
                          </div>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {invitations.data ? (
        <ListPagination
          page={page}
          count={invitations.data.count}
          hasPrevious={Boolean(invitations.data.previous)}
          hasNext={Boolean(invitations.data.next)}
          onPageChange={setPage}
        />
      ) : null}

      <ConfirmDialog
        open={pendingRevoke !== null}
        onOpenChange={open => {
          if (!open) setPendingRevoke(null);
        }}
        title={t('invitations:list.revokeTitle')}
        description={t('invitations:list.revokeDescription')}
        confirmLabel={t('invitations:list.revoke')}
        destructive
        onConfirm={() => (pendingRevoke ? onRevoke(pendingRevoke) : undefined)}
      />
    </div>
  );
}
